<template>
    <div class="w-full space-y-8" v-if="purchaseOrder">
        <ui-header :title="`Purchase Order #${purchaseOrder.reference}`" :subtitle="''">
        </ui-header>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <ui-data-grid-item label="Supplier" :value="purchaseOrder.supplier"
            ></ui-data-grid-item>
            <ui-data-grid-item label="Date" :value="moment(purchaseOrder.date).format('MMMM Do, YYYY')"
            ></ui-data-grid-item>
            <ui-data-grid-item label="Supplier Reference" :value="purchaseOrder.supplierReference"
            ></ui-data-grid-item>
        </div>
        <ui-data-grid>
            <ui-data-grid-item label="Additional Information" :value="purchaseOrder.additionalInformation"
                               wide></ui-data-grid-item>
        </ui-data-grid>
        <div class="mt-6">
            <label class="block text-sm font-medium text-gray-700">Items</label>
            <div
                class="flex border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                <div class="flex-1 border-r border-l border-gray-200 px-2 py-1">Description</div>
                <div class="w-16 -ml-px border-r border-gray-200 px-1.5 py-1">Qty</div>
                <div class="w-16 -ml-px border-r border-gray-200 px-1.5 py-1">UoM</div>
                <div class="w-24 -ml-px border-r border-gray-200 px-1.5 py-1">Rate</div>
                <div class="w-24 -ml-px border-r border-gray-200 px-1.5 py-1">Net</div>
                <div class="w-16 -ml-px border-r border-gray-200 px-1.5 py-1">Vat %</div>
                <div class="w-24 -ml-px border-r border-gray-200 px-1.5 py-1">Vat</div>
                <div class="w-24 -ml-px border-r border-gray-200 px-1.5 py-1">Total</div>
            </div>

            <div class="flex mb-px relative" v-for="item in purchaseOrder.items">
                <!-- Description. -->
                <div class="flex flex-col flex-1 m-0 relative">
                    <div class="flex flex-1 m-0 relative">
                        <div
                            class="flex flex-1 m-0 -ml-px -mt-px mr-px -mb-px border-l border-t border-b border-gray-200 shadow-none">
                            <div class="flex-col flex-grow">
                                <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'"
                                                        ref="autoresizable-description">
                    <textarea
                        placeholder="Item description..."
                        :read-only="true"
                        v-model="item.description"
                        class="text-sm text-gray-600 m-0 px-2 mt-0 pt-1 pb-1 placeholder:text-gray-400 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none"
                        type="text"
                        ref="description"
                        rows="1"
                    ></textarea>
                                </autoresizable-textarea>
                                <div class="flex flex-1 m-0 relative" v-if="item.packageReference">
                                    <div class="flex flex-grow my-1 text-xs px-1.5 pt-0.5 pm-0 text-gray-600">
                                        <div class="mx-1 flex-shrink font-bold">Package Reference :
                                        </div>
                                        <div class="ml-1 flex-shrink">{{ item.packageName }}
                                        </div>
                                    </div>
                                </div>
                                <div class="flex flex-1 m-0 relative" v-if="item.deliveryOption">
                                    <div class="flex flex-grow my-1 text-xs px-1.5 pt-0.5 pm-0 text-gray-600">
                                        <div class="ml-1 flex-shrink font-bold" v-if="item.deliveryOption.isDelivery">
                                            {{ item.deliveryOption.address ? 'Delivery to : ' : 'Delivery' }}
                                        </div>
                                        <div class="ml-1 flex-shrink font-bold" v-else>
                                            {{ item.deliveryOption.address ? 'Collection from : ' : 'Collection' }}
                                        </div>
                                        <div class="ml-1 flex-shrink whitespace-pre" v-if="item.deliveryOption.address"
                                             v-text="item.deliveryOption.address"></div>
                                        <div class="mx-0.5 flex-shrink">
                                            {{ item.deliveryOption.isDelivery ? 'on' : 'at' }}
                                        </div>
                                        <div class="mx-0.5 flex-grow font-bold"
                                             v-text="item.deliveryOption.date ? moment(item.deliveryOption.date).format('MMMM Do, YYYY') : 'ASAP'">
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="w-16 -ml-px flex">
                            <page-cell
                                class="text-sm -my-px ml-0 px-1 py-1.5 border border-gray-200"
                                placeholder="Qty..."
                                :read-only="true"
                                :useTextareaReadonly="true"
                                v-model="item.quantity"
                            ></page-cell>
                        </div>
                        <div class="w-16 -ml-px flex">
                            <page-cell
                                class="text-sm -my-px ml-0 px-1 py-1.5 border border-gray-200"
                                placeholder="UoM..."
                                :read-only="true"
                                :useTextareaReadonly="true"
                                v-model="item.unitOfMeasure"
                            ></page-cell>
                        </div>
                        <div class="w-24 -ml-px flex">
                            <page-cell
                                currency
                                class="text-sm -my-px ml-0 px-1 py-1.5 border border-gray-200"
                                :class="rateRounded?'text-gray-400':''"
                                placeholder="Rate..."
                                :read-only="true"
                                :useTextareaReadonly="true"
                                v-model="item.rate"
                            ></page-cell>
                        </div>
                        <div class="w-24 -ml-px flex">
                            <page-cell
                                currency
                                class="text-sm -my-px ml-0 px-1 py-1.5 border border-gray-200"
                                placeholder="Net..."
                                :read-only="true"
                                :useTextareaReadonly="true"
                                v-model="item.net"
                            ></page-cell>
                        </div>
                        <div class="w-16 -ml-px flex">
                            <page-cell
                                class="text-sm -my-px ml-0 px-1 py-1.5 border border-gray-200"
                                placeholder="Vat %..."
                                :read-only="true"
                                :useTextareaReadonly="true"
                                v-model="item.vatPercentage"
                            ></page-cell>
                        </div>
                        <div class="w-24 -ml-px flex">
                            <page-cell
                                currency
                                class="text-sm -my-px ml-0 px-1 py-1.5 border border-gray-200"
                                placeholder="Vat..."
                                :read-only="true"
                                :useTextareaReadonly="true"
                                v-model="item.vatValue"
                            ></page-cell>
                        </div>
                        <div class="w-24 -ml-px flex">
                            <page-cell
                                currency
                                class="text-sm -my-px ml-0 px-1 py-1.5 border border-gray-200"
                                placeholder="Total..."
                                :read-only="true"
                                :useTextareaReadonly="true"
                                v-model="item.value"
                            ></page-cell>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import UiDatePicker from "../../ui/UiDatePicker.vue";
import UiInput from "../../ui/UiInput.vue";
import PurchaseOrderItem from "../../procurement/purchase-orders/PurchaseOrderItem.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import moment from "moment";
import PageCell from "../../ui/PageCell.vue";
import PurchaseOrderPackageSearch from "../../procurement/purchase-orders/PurchaseOrderPackageSearch.vue";
import PurchaseOrderAddressSelect from "../../procurement/purchase-orders/PurchaseOrderAddressSelect.vue";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";

export default {
    computed: {
        moment() {
            return moment
        }
    },
    components: {
        AutoresizableTextarea,
        PurchaseOrderAddressSelect,
        PurchaseOrderPackageSearch,
        PageCell,
        UiDataGrid, UiDataGridItem, PurchaseOrderItem, UiInput, UiDatePicker
    },
    props: {
        purchaseOrder: Object,
    },
    data() {
        return {}
    },
    methods: {},
    async mounted() {
    }
}
</script>
